/**
 * Stable org record id for React keys and effects.
 * V2 org API (flatten_v2_to_v1_compat) omits legacy flat `id`; use organizationId first.
 *
 * @param {object | null | undefined} merchant
 * @returns {string | null}
 */
export function getMerchantRecordId(merchant) {
  if (!merchant || typeof merchant !== 'object' || Array.isArray(merchant)) return null;
  const normalizeId = (value) => {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return null;
  };
  return normalizeId(merchant.organizationId) || normalizeId(merchant.id);
}

/**
 * Produce a useful workspace fallback from human-readable organization names.
 * Leading punctuation is intentionally ignored so ".NGO Charities" becomes
 * "NC" instead of the visually broken "." fallback.
 *
 * @param {unknown} name
 * @returns {string}
 */
export function getOrganizationInitials(name) {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .map((word) => word.match(/[A-Za-z0-9]/)?.[0] || '')
    .filter(Boolean)
    .slice(0, 2);

  return words.map((letter) => letter.toUpperCase()).join('') || 'O';
}

const trimmedUrl = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Return logo candidates in display priority order. The Sidebar advances to
 * the next candidate when an image fails, so a stale merchant URL can still
 * fall back to the conventional CDN asset and then Clerk's organization image.
 *
 * @param {object | null | undefined} merchant
 * @param {string | null | undefined} clerkOrganizationImageUrl
 * @param {string | null | undefined} imageBaseUrl
 * @returns {string[]}
 */
export function getMerchantLogoCandidates(merchant, clerkOrganizationImageUrl, imageBaseUrl) {
  const candidates = [
    trimmedUrl(merchant?.logoUrl),
    trimmedUrl(merchant?.organizationLogoUrl),
    trimmedUrl(merchant?.logoImageUrl),
  ];

  const merchantName = trimmedUrl(merchant?.merchantName);
  const baseUrl = trimmedUrl(imageBaseUrl).replace(/\/+$/, '');
  if (merchantName && baseUrl) {
    const cdnLogo = `${baseUrl}/logo/${merchantName}_Logo.png`;
    const cacheVersion =
      merchant?.updatedAt ?? merchant?.lastUpdated ?? getMerchantRecordId(merchant);
    candidates.push(
      cacheVersion != null ? `${cdnLogo}?t=${encodeURIComponent(String(cacheVersion))}` : cdnLogo,
    );
  }

  candidates.push(trimmedUrl(clerkOrganizationImageUrl));
  return [...new Set(candidates.filter(Boolean))];
}
